import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useJurisdictions } from "@/components/Jurisdictions";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Admin view of a listing's official tax-zone lookup: flags, recheck and manual confirm.
export function TaxZoneReview({ l }) {
  const qc = useQueryClient();
  const { data: list = [] } = useJurisdictions();
  const z = l.tax_zone;
  const [pick, setPick] = useState(z?.zone || l.jurisdiction || "");
  if (!z) return null;
  const call = async (path, body) => {
    try {
      await api.post(`/admin/listings/${l.listing_id}/tax-zone/${path}`, body);
      toast.success(path === "confirm" ? "Tax zone confirmed" : "Rechecked against the official maps");
      qc.invalidateQueries({ queryKey: ["admin-listings"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const ok = z.status === "verified";
  const name = (k) => list.find((j) => j.key === k)?.name || "none";
  return (
    <div className={`mt-2 rounded-xl border p-3 ${ok ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid={`admin-tax-zone-${l.listing_id}`}>
      <p className="font-semibold" data-testid={`admin-tax-zone-status-${l.listing_id}`}>Tax zone: {name(z.zone)} · {ok ? (z.confirmed_by ? "confirmed by admin" : "verified by official map") : "NEEDS REVIEW"}</p>
      <p className="break-words text-xs text-stone-600">Address: {l.street_address || "—"}, {l.city} {l.zip_code}{z.matched_address && ` → map match: ${z.matched_address}`}{z.official_rate != null && ` · official rate ${z.official_rate}%`}</p>
      {l.declared_zone && <p className="text-xs text-stone-600">Owner chose: {name(l.declared_zone)}</p>}
      {!ok && !!z.flags?.length && <ul className="mt-1 list-disc pl-5 text-xs text-amber-900">{z.flags.map((f) => <li key={f}>{f}</li>)}</ul>}
      {!ok && z.flags?.some((f) => f.includes("isn't a supported")) && <p className="mt-1 text-xs font-semibold text-amber-900" data-testid={`admin-tax-zone-unsupported-${l.listing_id}`}>This area isn't supported yet. Don't confirm a zone; use "Needs changes" instead.</p>}
      <div className="mt-2 flex flex-wrap gap-2">
        <select className="w-full max-w-full rounded-lg border bg-white px-2 py-1 text-xs sm:w-auto" value={pick} onChange={(e) => setPick(e.target.value)} data-testid={`admin-tax-zone-select-${l.listing_id}`}>
          <option value="">Choose zone</option>
          {list.map((j) => <option key={j.key} value={j.key}>{j.name} ({j.rate}%)</option>)}
        </select>
        <Button size="sm" className="h-7 rounded-full text-xs" disabled={!pick} onClick={() => call("confirm", { zone: pick })} data-testid={`admin-tax-zone-confirm-${l.listing_id}`}>Confirm zone</Button>
        <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" onClick={() => call("recheck")} data-testid={`admin-tax-zone-recheck-${l.listing_id}`}>Recheck maps</Button>
      </div>
    </div>
  );
}

// Who must collect and pay room tax, plus rules still unverified.
export function TaxComplianceNotice() {
  const { data } = useQuery({ queryKey: ["tax-compliance"], queryFn: () => api.get("/admin/tax-compliance").then((r) => r.data) });
  if (!data) return null;
  return (
    <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950" data-testid="tax-compliance-notice">
      <p className="font-semibold">Who collects and pays room tax</p>
      <p className="mt-1">{data.remittance}</p>
      {Object.values(data.unverified).map((u) => <p key={u} className="mt-1 text-xs">Unverified: {u}</p>)}
    </div>
  );
}
